import { useRef, useState } from "react";
import { useClerk, useUser } from "@clerk/react";
import { LogOut, User as UserIcon } from "lucide-react";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";

type UserMenuProps = {
  collapsed?: boolean;
};

// Custom user menu over Clerk's API. Replaces <UserButton> for the
// same reason as <OrganizationSwitcher> — the upstream component owns
// its text colors and ignores the Gated tokens. The dropdown opens
// upward since the trigger sits at the very bottom of the sidebar.
export function UserMenu({ collapsed = false }: UserMenuProps) {
  const clerk = useClerk();
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => setOpen(false));

  if (!user) return null;

  const fullName = user.fullName ?? user.firstName ?? "Signed in";
  const email = user.primaryEmailAddress?.emailAddress ?? "";
  const initial = (user.firstName?.[0] ?? fullName[0] ?? "?").toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={collapsed ? fullName : undefined}
        className={
          collapsed
            ? "w-9 h-9 mx-auto flex items-center justify-center rounded-md hover:bg-bg-3 transition-colors"
            : "w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-bg-3 transition-colors text-left"
        }
      >
        {user.imageUrl ? (
          <img
            src={user.imageUrl}
            alt=""
            width={28}
            height={28}
            className="rounded-md shrink-0"
          />
        ) : (
          <span className="size-7 rounded-md bg-fg-1 text-bg-1 flex items-center justify-center text-caption font-semibold shrink-0">
            {initial}
          </span>
        )}
        {!collapsed ? (
          <span className="flex flex-col min-w-0 flex-1">
            <span className="text-small text-fg-1 font-medium truncate">
              {fullName}
            </span>
            <span className="font-mono text-compact text-fg-3 truncate">
              {email}
            </span>
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute left-0 bottom-full mb-1.5 z-30 w-56 rounded-md border border-border-1 bg-bg-1 shadow-[var(--shadow-2)] py-1.5"
        >
          <div className="px-3 py-1.5">
            <div className="text-small text-fg-1 font-medium truncate">
              {fullName}
            </div>
            <div className="font-mono text-[11px] text-fg-3 truncate">
              {email}
            </div>
          </div>
          <div className="my-1 h-px bg-border-1" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              clerk.openUserProfile();
            }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-small text-fg-2 hover:bg-bg-2 hover:text-fg-1 text-left"
          >
            <UserIcon size={13} strokeWidth={1.5} />
            Manage account
          </button>
          <div className="my-1 h-px bg-border-1" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void clerk.signOut();
            }}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-small text-sev-crit hover:bg-bg-2 text-left"
          >
            <LogOut size={13} strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

